
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {Shield, Key, CheckCircle, XCircle, Calendar, User} from 'lucide-react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

interface License {
  _id: string
  licenseKey: string
  customerName: string
  customerEmail: string
  licenseType: string
  status: string
  expiryDate: string
  maxDownloads: number
  downloadCount: number
  createdAt: string
}

interface LicenseGateProps {
  children: React.ReactNode
}

const LicenseGate: React.FC<LicenseGateProps> = ({ children }) => {
  const { isAdmin } = useAuth()
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [licenseKey, setLicenseKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [license, setLicense] = useState<License | null>(null)

  useEffect(() => {
    // 관리자는 자동으로 접근 허용
    if (isAdmin) {
      setIsAuthorized(true)
      return
    }

    // 세션에서 라이센스 정보 확인
    const savedLicense = sessionStorage.getItem('activeLicense')
    if (savedLicense) {
      try {
        const licenseData = JSON.parse(savedLicense)
        if (new Date(licenseData.expiryDate) > new Date()) {
          setIsAuthorized(true)
          setLicense(licenseData)
        } else {
          sessionStorage.removeItem('activeLicense')
        }
      } catch (error) {
        sessionStorage.removeItem('activeLicense')
      }
    }
  }, [isAdmin])

  const validateLicense = async () => {
    if (!licenseKey.trim()) {
      toast.error('라이센스 키를 입력해주세요')
      return
    }

    setLoading(true)
    try {
      const { list } = await lumi.entities.licenses.list({
        filter: {
          licenseKey: licenseKey.trim(),
          status: 'active'
        },
        limit: 1
      })

      if (list && list.length > 0) {
        const foundLicense = list[0]
        
        // 만료일 체크
        if (new Date(foundLicense.expiryDate) <= new Date()) {
          toast.error('라이센스가 만료되었습니다')
          return
        }

        // 다운로드 제한 체크
        if (foundLicense.maxDownloads > 0 && foundLicense.downloadCount >= foundLicense.maxDownloads) {
          toast.error('다운로드 한도에 도달했습니다')
          return
        }

        // 인증 성공
        setLicense(foundLicense)
        setIsAuthorized(true)
        sessionStorage.setItem('activeLicense', JSON.stringify(foundLicense))
        toast.success(`${foundLicense.customerName}님, 자료실에 오신 것을 환영합니다!`)
      } else {
        toast.error('유효하지 않은 라이센스 키입니다')
      }
    } catch (error) {
      console.error('라이센스 검증 실패:', error)
      toast.error('라이센스 검증 중 오류가 발생했습니다')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      validateLicense()
    }
  }

  const getLicenseTypeColor = (type: string) => {
    switch (type) {
      case 'trial': return 'text-gray-600 bg-gray-100'
      case 'standard': return 'text-blue-600 bg-blue-100'
      case 'premium': return 'text-purple-600 bg-purple-100'
      case 'enterprise': return 'text-green-600 bg-green-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getLicenseTypeName = (type: string) => {
    switch (type) {
      case 'trial': return '체험판'
      case 'standard': return '스탠다드'
      case 'premium': return '프리미엄'
      case 'enterprise': return '엔터프라이즈'
      default: return type
    }
  }

  if (isAuthorized) {
    return (
      <div>
        {license && !isAdmin && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="text-green-500" size={20} />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-green-800">{license.customerName}님</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLicenseTypeColor(license.licenseType)}`}>
                    {getLicenseTypeName(license.licenseType)}
                  </span>
                </div>
                <p className="text-sm text-green-600">
                  만료일: {new Date(license.expiryDate).toLocaleDateString()}
                  {license.maxDownloads > 0 && (
                    <span className="ml-4">
                      다운로드: {license.downloadCount}/{license.maxDownloads}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
        {children}
      </div>
    )
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-md mx-auto py-20 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <div className="text-center mb-8">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="text-blue-600" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">자료실 접근 인증</h1>
            <p className="text-gray-600">
              자료실의 파일 다운로드를 위해서는 유효한 라이센스 키 인증이 필요합니다.
            </p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                라이센스 키
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="라이센스 키를 입력하세요"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                />
              </div>
            </div>

            <button
              onClick={validateLicense}
              disabled={loading || !licenseKey.trim()}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <CheckCircle size={20} />
                  <span>자료실 접근 인증</span>
                </>
              )}
            </button>
          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">라이센스가 없으신가요?</h3>
            <p className="text-sm text-gray-600 mb-3">
              자료실 이용을 위한 라이센스는 영업팀에 문의해 주시기 바랍니다.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              <User size={16} className="mr-1" />
              영업팀 문의하기
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default LicenseGate
